import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { WarningIcon } from "../../asset/image";
import {
  useSelectTheme,
  useCreateDispatchActions,
  useSelectSessionState,
  SERVICE_UNAVAILABLE_STATUS_LIST,
} from "../../store";
import {
  v,
  debug,
  useForm,
  useTranslation,
  RIF,
  RequestResult,
  useLoginRedirect,
  useRateLimitWait,
  withLoginRedirect,
} from "../../lib";
import { LabelBox, Legend, Input, Button, RateLimitNotice } from "..";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { DeployStage, Env } from "../../env";

class RecaptchaNotReadyError extends Error {
  constructor() {
    super("Recaptcha is not ready");
    this.name = "RecaptchaNotReadyError";
  }
}

const schema = v.object({
  email: v.string().required(),
  password: v.string().min(8).required(),
});

export const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirect, completeLogin } = useLoginRedirect();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { pad, color } = useSelectTheme();

  const { doREQUEST_IDENTITY_AUTHENTICATE, doSESSION_END_REASON_SET } = useCreateDispatchActions();
  const { endReason } = useSelectSessionState();
  const [requestResult, setRequestResult] = useState<RequestResult | null>(null);
  const [inputComplete, setInputComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const { waitSeconds, waitIfRateLimited } = useRateLimitWait();

  // the first interaction with the form answers the login page's sign-out notice. It hangs
  // off the field's own `onChange` rather than off a value effect, because the values also move
  // on mount and on a prefilled email, and neither of those is the user saying "I read it".
  const clearSignOutReason = () => {
    if (endReason) doSESSION_END_REASON_SET(null);
  };

  const locationState: { email: string | undefined } = useLocation().state as any;
  const [emailValue, setEmailValue] = useState(""); // order to auto fill up the email to next page
  const { handleSubmit, watch, control } = useForm({ schema });

  useEffect(() => {
    if (locationState && locationState?.email) setEmailValue(locationState.email);
    if (watch("email")) setEmailValue(watch("email"));
  }, [locationState, watch("email")]);

  const onSubmit = async (data: any) => {
    debug("LoginForm.onSubmit", { data });
    // the notice describes the attempt that failed, not the one now going out
    setUnavailable(false);
    const recaptchaRequest = async () => {
      if (Env.shared.stageIs(DeployStage.Test)) {
        return true;
      }
      if (!executeRecaptcha) {
        throw new RecaptchaNotReadyError();
      }
      const token = await executeRecaptcha("login");
      return token;
    };

    await recaptchaRequest()
      .then((token) => {
        if (token) {
          doREQUEST_IDENTITY_AUTHENTICATE({
            setRequestResult,
            payload: data,
          });
        }
      })
      .catch((error) => {
        if (error instanceof RecaptchaNotReadyError) {
          return alert(error.message);
        }
        throw error;
      });
  };

  const onError = (error: any) => {
    debug("LoginForm.onError", { error });
    setHasError(true);
  };

  // what the failure was, in the user's words. Only a 401 is the user's fault; a 429 asks for a wait
  // and holds the button until it is over, and every server or transport failure gets the same
  // inline unavailable line with the button left enabled. The status set is
  // `SERVICE_UNAVAILABLE_STATUS_LIST`, shared with the ordinary-503 notice so the two cannot drift,
  // plus 500.
  //
  // This form once carried an `alert('server error')` branch for a 500. It was unreachable: the
  // `!success` check caught every failed request before it, so a 500 showed the credentials
  // message. Do not revive it -- a 500 is a server fault and reads as one.
  useEffect(() => {
    // nothing has been submitted yet; `statusCode` is absent here, not null-because-no-answer
    if (!requestResult) return;

    if (!requestResult.success) {
      if (waitIfRateLimited(requestResult)) return;
      const statusCode = requestResult.result?.statusCode ?? null;
      if (statusCode === 500 || SERVICE_UNAVAILABLE_STATUS_LIST.includes(statusCode))
        return setUnavailable(true);
      return setHasError(true);
    }

    if (requestResult?.result.type === "MFA_REQUIRED")
      return navigate(
        withLoginRedirect(
          `/account_setup/verify_code_page?email=${encodeURIComponent(emailValue)}&verify_source=mfaLogin`,
          redirect,
        ),
      );
    completeLogin();
  }, [requestResult]);

  useEffect(() => {
    if (emailValue !== "" || watch("password") !== "") {
      setHasError(false);
      setUnavailable(false);
    }
    if (emailValue !== "" && watch("password")?.length >= 8) return setInputComplete(true);
    setInputComplete(false);
  }, [emailValue, watch("password")]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      css={{
        marginTop: "50px",
        width: "446px",
        padding: "40px 30px",
        background: color.white,
        borderRadius: "5px",
        boxShadow: "0px 4px 12px 0px #D4D4D440",
      }}
    >
      <Legend css={{ marginBottom: "16px", fontSize: "20px" }}>Log In</Legend>
      <div css={{ marginBottom: "40px" }}>
        Don't have an account?
        <Link to="/signup" css={{ color: color.primary }}>
          {" "}
          Sign up for free
        </Link>
      </div>
      <LabelBox
        {...{
          label: t("sign_in.form_field_email_label"),
          id: "email-input",
        }}
      >
        <Controller
          control={control}
          name="email"
          render={({ field: { name, value, onChange } }) => (
            <Input
              {...{
                name: name,
                value: emailValue,
                onChange: (event: any) => {
                  clearSignOutReason();
                  onChange(event);
                },
              }}
              hasError={hasError}
              type="email"
              id={"email-input"}
              placeholder={t("sign_in.form_field_email_placeholder")}
              css={{ marginBottom: "27px" }}
            />
          )}
        />
      </LabelBox>
      <LabelBox
        {...{
          label: t("sign_in.form_field_password_label"),
          id: "password-input",
        }}
      >
        <Controller
          control={control}
          name="password"
          render={({ field: { name, value, onChange } }) => (
            <Input
              {...{
                name: name,
                value: value,
                onChange: (event: any) => {
                  clearSignOutReason();
                  onChange(event);
                },
              }}
              hasError={hasError}
              type="password"
              id={"password-input"}
              placeholder={t("sign_in.form_field_password_placeholder")}
            />
          )}
        />
      </LabelBox>

      <div css={{ marginTop: "18px" }}>
        <Link
          to="/account_setup/forget_password"
          state={{ email: emailValue }}
          css={{
            color: color.primary,
            textDecoration: "none",
          }}
        >
          {t("sign_in.button_forgot_password")}
        </Link>
      </div>

      {/* In normal flow, unlike the credentials line below: this one is a sentence rather than a
          phrase, so it has to be free to wrap instead of sitting on top of the button row. */}
      {RIF(
        unavailable,
        <div
          data-testid="login_form_unavailable_message"
          role="status"
          aria-live="polite"
          css={{ display: "flex", alignItems: "flex-start", marginTop: "24px" }}
        >
          <img src={WarningIcon} width="20" css={{ flex: "none", marginTop: "2px" }} />
          <span css={{ marginLeft: "8px", color: color.warning }}>
            {t("connection.unavailable_message")}
          </span>
        </div>,
      )}
      {RIF(waitSeconds, (seconds) => (
        <RateLimitNotice waitSeconds={seconds} css={{ marginTop: "24px" }} />
      ))}

      <div css={{ display: "flex", alignItems: "center", marginTop: "32px", position: "relative" }}>
        {RIF(
          hasError,
          <div css={{ display: "flex", alignItems: "center", position: "absolute", top: "-40px" }}>
            <img src={WarningIcon} width="20" />
            <span css={{ marginLeft: "8px", color: color.warning }}>
              Incorrect email and password combination
            </span>
          </div>,
        )}

        <Button
          type="submit"
          btnPadding="large"
          disabled={!inputComplete || hasError || waitSeconds !== null}
        >
          Log In
        </Button>
      </div>
    </form>
  );
};
